import * as THREE from 'three';
import { ModelLibrary, type ModelInstance } from './modelLibrary';
import { CharacterMotion } from './characterMotion';
import { fitTakaseEyeLevel } from './characterFit';

export const TAKASE_MODEL = 'assets/models/characters/takase.glb';

/** Single-NPC trial. Loading failure and F7 both retain the original visual. */
export class CharacterModels {
  private generation = 0;
  private instance?: ModelInstance;
  private slot?: THREE.Object3D;
  private fallback: { object: THREE.Object3D; visible: boolean }[] = [];
  private motion?: CharacterMotion;
  private disposed = false;
  private enabled = true;
  private previous?: number;
  private speaker: string | null = null;
  private line = '';
  private slotRest?: THREE.Quaternion;
  private facingTarget?: THREE.Quaternion;
  status = '高瀬: 読込待ち';

  constructor(private readonly library: Pick<ModelLibrary, 'acquire' | 'dispose'> = new ModelLibrary(),
    private readonly onChange: () => void = () => {}) {}

  async attach(root: THREE.Object3D): Promise<void> {
    this.clear();
    if (this.disposed) return;
    root.traverse(object => { if (object.name === 'character-slot:takase') this.slot = object; });
    const slot = this.slot;
    if (!slot) { this.status = '高瀬: 対象なし'; return; }
    this.slotRest = slot.quaternion.clone();
    const generation = this.generation;
    this.status = '高瀬: GLB読込中';
    try {
      const instance = await this.library.acquire(`${import.meta.env.BASE_URL}${TAKASE_MODEL}`);
      if (this.disposed || generation !== this.generation) { instance.release(); return; }
      try { fitTakaseEyeLevel(instance.root); }
      catch (error) { instance.release(); throw error; }
      instance.root.traverse(object => {
        if (object instanceof THREE.Mesh) {
          object.castShadow = object.receiveShadow = true;
          if (object instanceof THREE.SkinnedMesh) {
            object.computeBoundingSphere();
            // Motion is bounded (<2cm at eyelids, <0.1m at the head).
            if (object.boundingSphere) object.boundingSphere.radius += .15;
          }
        }
      });
      this.fallback = slot.children.map(object => ({ object, visible: object.visible }));
      this.instance = instance;
      slot.add(instance.root);
      this.motion = new CharacterMotion(instance.root, slot);
      this.motion.dialogue(this.speaker, this.line);
      this.applyVisibility();
    } catch (error) {
      if (generation !== this.generation || this.disposed) return;
      this.status = '高瀬: 読込失敗・簡易表示';
      console.warn('[character] 高瀬は元の表示で続行します', error);
    }
  }

  setEnabled(enabled: boolean): void { this.enabled = enabled; this.applyVisibility(); }
  toggle(): void { this.setEnabled(!this.enabled); }
  dialogue(speaker: string | null, line: string): void {
    this.speaker = speaker; this.line = line;
    this.motion?.dialogue(speaker, line);
  }
  /** One explicit interaction turns the visual about its feet. Capture the
   * camera now: idle NPCs must not continuously follow a passing player.
   * The slot also contains the fallback, so loading/F7 cannot undo the turn.
   */
  facePlayer(interactableId: string, camera: THREE.Camera): boolean {
    const slot = this.slot;
    if (interactableId !== 'int-takase' || !slot?.parent || this.disposed) return false;
    const target = camera.getWorldPosition(new THREE.Vector3());
    slot.parent.worldToLocal(target).sub(slot.position);
    if (![target.x, target.z].every(Number.isFinite) || Math.hypot(target.x, target.z) < .001) return false;
    this.facingTarget = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0), Math.atan2(target.x, target.z));
    return true;
  }
  update(camera: THREE.Camera, now: number): void {
    const dt = this.previous === undefined ? 0 : (now - this.previous) / 1000;
    this.previous = now;
    if (this.slot && this.facingTarget) {
      const step = Number.isFinite(dt) ? THREE.MathUtils.clamp(dt, 0, .05) : 0;
      this.slot.quaternion.rotateTowards(this.facingTarget, Math.PI * step);
      if (this.slot.quaternion.angleTo(this.facingTarget) < .001) {
        this.slot.quaternion.copy(this.facingTarget);
        this.facingTarget = undefined;
      }
    }
    if (this.enabled) this.motion?.update(dt, camera);
  }
  private applyVisibility(): void {
    if (!this.instance) return;
    this.instance.root.visible = this.enabled;
    for (const entry of this.fallback) entry.object.visible = this.enabled ? false : entry.visible;
    this.status = this.enabled ? '高瀬: GLB / F7で比較' : '高瀬: 元の簡易表示 / F7で比較';
    this.onChange();
  }
  clear(): void {
    this.generation++;
    if (this.slot && this.slotRest) this.slot.quaternion.copy(this.slotRest);
    this.slotRest = undefined; this.facingTarget = undefined;
    this.instance?.root.removeFromParent();
    this.instance?.release();
    for (const entry of this.fallback) entry.object.visible = entry.visible;
    this.instance = undefined; this.slot = undefined; this.fallback = []; this.motion = undefined;
    this.previous = undefined;
  }
  dispose(): void { this.disposed = true; this.clear(); this.library.dispose(); }
}
